import { OrganizeService } from '@affine/core/modules/organize';
import { TemplateDocService } from '@affine/core/modules/template-doc';
import type { Workspace } from '@affine/core/modules/workspace';
import { WorkspacesService } from '@affine/core/modules/workspace';
import { useService } from '@toeverything/infra';
import { useEffect, useState } from 'react';

export interface WorkspaceTemplateOption {
  /** `${workspaceId}:${folderId}` */
  key: string;
  workspaceId: string;
  workspaceName: string;
  folderId: string;
  folderName: string;
}

/** per-workspace wait budget so a slow/offline workspace does not stall the list */
const TEMPLATE_WAIT_TIMEOUT_MS = 8000;

/**
 * The folder tree and its template flags live in these db sub-docs, not in the
 * workspace root doc. A workspace connects them lazily and loads them
 * asynchronously, so reading the tables right after the root doc is ready
 * yields nothing at all.
 */
const TEMPLATE_DB_DOC_IDS = ['db$folders', 'db$folderProperties'];

/**
 * Waits until the folder templates of a freshly opened workspace can actually
 * be read. Callers must have resolved the workspace-db backed services first —
 * that is what connects the sub-docs in the first place.
 */
export const waitForTemplateData = async (workspace: Workspace) => {
  const engine = workspace.engine.doc;
  await Promise.race([
    Promise.all([
      engine.waitForDocReady(workspace.id),
      ...TEMPLATE_DB_DOC_IDS.flatMap(docId => [
        engine.waitForDocLoaded(docId),
        // a workspace that was never opened on this device has nothing in
        // local storage, so the rows only show up once the server answers
        engine.waitForSynced(docId),
      ]),
    ]),
    new Promise(resolve => setTimeout(resolve, TEMPLATE_WAIT_TIMEOUT_MS)),
  ]);
};

/**
 * Collects folder templates from all workspaces of the user. Each workspace
 * is opened lazily (its folder-template flags live in the workspace db) and
 * disposed again once read.
 *
 * Callers are expected to mount this only while the template picker is
 * visible — opening every workspace is not free. Workspaces are read in
 * parallel so one slow workspace only costs its own wait budget.
 */
export const useWorkspaceTemplates = () => {
  const workspacesService = useService(WorkspacesService);
  const [options, setOptions] = useState<WorkspaceTemplateOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const workspaces = workspacesService.list.workspaces$.value;
      const collected = (
        await Promise.all(
          workspaces.map(async ws => {
            const opened = workspacesService.openByWorkspaceId(ws.id);
            if (!opened) return [];
            try {
              // resolving these connects the workspace db sub-docs
              const scope = opened.workspace.scope;
              const templateDocService = scope.get(TemplateDocService);
              const organizeService = scope.get(OrganizeService);
              await waitForTemplateData(opened.workspace);
              if (cancelled) return [];
              const profile = workspacesService.getProfile(ws);
              return templateDocService.folderList
                .getTemplateFolderIds()
                .map(folderId => {
                  const node =
                    organizeService.folderTree.folderNode$(folderId).value;
                  return {
                    key: `${ws.id}:${folderId}`,
                    workspaceId: ws.id,
                    workspaceName: profile.name$.value ?? 'Workspace',
                    folderId,
                    folderName: node?.name$.value || 'Untitled',
                  };
                });
            } catch (err) {
              console.warn(
                `[create-workspace] failed to read templates of workspace ${ws.id}`,
                err
              );
              return [];
            } finally {
              opened.dispose();
            }
          })
        )
      ).flat();
      if (!cancelled) {
        collected.sort((a, b) => a.folderName.localeCompare(b.folderName));
        setOptions(collected);
        setLoading(false);
      }
    };

    load().catch(err =>
      console.warn('[create-workspace] failed to load templates', err)
    );
    return () => {
      cancelled = true;
    };
  }, [workspacesService]);

  return { options, loading };
};
